/**
 * Counter Registry
 *
 * Central registry for all exercise counting algorithms.
 * Allows easy addition of new counters and selection via UI.
 */

import { XGBoostJumpCounter } from './XGBoostJumpCounter.js';
import { XGBoostStrokeCounter } from './XGBoostStrokeCounter.js';
import { XGBoostSoccerJuggleCounter } from './XGBoostSoccerJuggleCounter.js';

// Registry of all available counters
const counters = new Map();

/**
 * Register a counter class
 * @param {class} CounterClass - Counter class extending BaseCounter
 */
export function registerCounter(CounterClass) {
  if (!CounterClass.id) {
    throw new Error('Counter class must have a static id property');
  }
  counters.set(CounterClass.id, CounterClass);
}

/**
 * Get a counter class by ID
 * @param {string} id - Counter ID
 * @returns {class|null} Counter class or null if not found
 */
export function getCounterClass(id) {
  return counters.get(id) || null;
}

/**
 * Create a new counter instance by ID
 * @param {string} id - Counter ID
 * @returns {BaseCounter} New counter instance
 */
export function createCounter(id) {
  const CounterClass = counters.get(id);
  if (!CounterClass) {
    throw new Error(`Unknown counter type: ${id}`);
  }
  return new CounterClass();
}

/**
 * Get list of all available counters with metadata
 * @returns {Array<{id: string, displayName: string, description: string}>}
 */
export function getAvailableCounters() {
  return Array.from(counters.values()).map(CounterClass => ({
    id: CounterClass.id,
    displayName: CounterClass.displayName,
    description: CounterClass.description
  }));
}

/**
 * Get counter by ID or return default
 * @param {string} id - Counter ID
 * @param {string} defaultId - Default counter ID if not found
 * @returns {BaseCounter} Counter instance
 */
export function createCounterWithDefault(id, defaultId = 'skipping') {
  try {
    return createCounter(id);
  } catch {
    console.warn(`Counter '${id}' not found, using default '${defaultId}'`);
    return createCounter(defaultId);
  }
}

// Register built-in counters
registerCounter(XGBoostJumpCounter);       // Uses ML model with pose temporal features
registerCounter(XGBoostStrokeCounter);     // Uses ML model with YOLO temporal features
registerCounter(XGBoostSoccerJuggleCounter); // Uses ML model with ball temporal features

// Export for external registration of custom counters
export { registerCounter as register };
